import { React, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import Checkbox from "@mui/material/Checkbox";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  RenderIf,
  ReactSelect,
  RichTextEditor,
  DatePickers,
} from "shared/components";
import { Download, Add } from "shared/components/Icons";
import { formatDate } from "shared/helper/formatDate";
import {
  useCreateCommunityEventMutation,
  useGetAllCommunityEventTypeWithoutFilterQuery,
} from "redux/api/community";
import { dateConvertItem } from "shared/helper/dateConvert";
import { options, initialValues } from "./data";
import "./Modal.scss";
const label = { inputProps: { "aria-label": "Checkbox demo" } };

const CreateCommunityEvents = ({ show, onHide, refetch }) => {
  const { communityId } = useParams();
  const { t } = useTranslation();
  const [file, setFile] = useState("");
  const [imageSrc, setImageSrc] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [fromClock, setFromClock] = useState(null);
  const [toClock, setToClock] = useState(null);
  const [date, setDate] = useState(null);
  const [eventTypeOption, setEventTypeOption] = useState(null);
  const [selectedValue, setSelectedValue] = useState(false);
  const [content, setContent] = useState();

  const handleChange = (event) => setIsChecked(event.target.checked);
  const imagePreview = (e) => {
    let reader = new FileReader();
    let file = e.target.files[0];
    setFile(file);
    formik.setFieldValue("file", file);

    reader.onloadend = () => {
      setImageSrc(reader.result);
    };

    reader.readAsDataURL(file);
  };
  const selectedValues = options?.map((type) => ({
    value: type.value,
    label: type.label,
  }));
  function handleChangeValue(value) {
    setSelectedValue(value);
    formik.setFieldValue("eventFormat", value.value);
  }
  const { data: eventType } = useGetAllCommunityEventTypeWithoutFilterQuery();
  const eventTypes = eventType?.data;
  const eventTypeOptions = eventTypes?.map((type) => ({
    value: type.id,
    label: type.title,
  }));
  function handleChangeReason(value) {
    setEventTypeOption(value);
    formik.setFieldValue("communityEventTypeId", value.value);
  }
  const onClick = (e) => {
    setImageSrc("");
    setFile("");
  };

  const handleContentChange = (content) => setContent(content);
  const handleEnter = (event) => {
    if (event.key.toLowerCase() === "enter") {
      const form = event.target.form;
      const index = [...form].indexOf(event.target);
      form.elements[index + 1].focus();
      event.preventDefault();
    }
  };

  const [createEvent, { isLoading, isSuccess }] =
    useCreateCommunityEventMutation();

  const CreateSchema = Yup.object().shape({
    title: Yup.string().required("Please fill the field"),
    description: Yup.string().required("Please fill the field"),
    eventFormat: Yup.string().required("Please fill the field"),
    communityEventTypeId: Yup.string().required("Please fill the field"),
    fromClock: Yup.string().required("Please fill the field"),
    toClock: Yup.string().required("Please fill the field"),
    link: Yup.string().required("Please fill the field"),
    date: Yup.string().required("Please fill the field"),
    file: Yup.string().required("Please fill the field"),
  });

  const getInputClasses = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return true;
    }
    if (formik.touched[fieldName] && !formik.errors[fieldName]) {
      return false;
    }

    return false;
  };

  const form = useRef();
  const formik = useFormik({
    initialValues,
    validationSchema: CreateSchema,
    onSubmit: (values) => {
      const formData = new FormData(form.current);
      formData.append("title", String(values.title));
      formData.append("link", String(values.link));
      formData.append("description", content);

      const selectedTimeFrom = values.fromClock
        ? values.fromClock.toLocaleTimeString()
        : null;
      const selectedTimeTo = values.toClock
        ? values.toClock.toLocaleTimeString()
        : null;

      formData.append("fromClock", selectedTimeFrom);
      formData.append("toClock", selectedTimeTo);
      formData.append("promoted", isChecked);
      formData.append("communityId", communityId);

      formData.append("eventFormat", String(values.eventFormat));
      formData.append(
        "communityEventTypeId",
        String(values.communityEventTypeId)
      );
      formData.append("date", dateConvertItem(values.date));
      formData.append("file", file);
      createEvent(formData);
    },
  });

  const { resetForm } = formik;

  const onHideModal = () => {
    onHide();
    resetForm();
    setFile("");
    setContent("");
  };

  useEffect(() => {
    if (isSuccess) {
      refetch();
      onHide();
      resetForm();
    }
  }, [isLoading]);

  return (
    <Modal
      size="lg"
      centered
      keyboard={false}
      backdrop="static"
      show={show}
      onHide={onHideModal}>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{t("createEvent")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="body-div">
            <div className="w-100 col-12 col-md-12 col-lg-12">
              <input
                id="title"
                name="title"
                label={t("title")}
                placeholder={t("title")}
                className="w-100"
                error={getInputClasses("title")}
                {...formik.getFieldProps("title")}
                onKeyDown={handleEnter}
              />
              <RenderIf
                condition={formik.touched.title && formik.errors.title}
                renderElse={null}>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.title}</div>
                </div>
              </RenderIf>
            </div>
            <div className="w-100 col-12 col-md-12 col-lg-6 mt-3 mb-3">
              <div className="d-flex align-items-center">
                <Checkbox
                  {...label}
                  color="success"
                  onChange={handleChange}
                  checked={isChecked}
                />
                <h6 className="mb-0">{t("promoted")}</h6>
              </div>
            </div>
            <div className="col-12 col-md-12 col-lg-12 w-100 mb-3">
              <ReactSelect
                isSearchable={false}
                options={selectedValues}
                onChange={handleChangeValue}
                single={selectedValue}
                placeholder={t("eventformat")}
                inputId="eventFormat"
              />
              <RenderIf
                condition={
                  formik.touched.eventFormat && formik.errors.eventFormat
                }
                renderElse={null}>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.eventFormat}
                  </div>
                </div>
              </RenderIf>
            </div>
            <div className="w-100 col-12 col-md-12 mb-3 col-lg-12">
              <input
                id="link"
                name="link"
                label={t("link")}
                placeholder={t("link")}
                className="w-100"
                error={getInputClasses("link")}
                {...formik.getFieldProps("link")}
                onKeyDown={handleEnter}
              />
              <RenderIf
                condition={formik.touched.link && formik.errors.link}
                renderElse={null}>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.link}</div>
                </div>
              </RenderIf>
            </div>
            <div className="col-12 col-md-12 col-lg-12 w-100">
              <ReactSelect
                isSearchable={false}
                options={eventTypeOptions}
                onChange={handleChangeReason}
                single={eventTypeOption}
                placeholder={t("eventType")}
                inputId="communityEventTypeId"
              />
              <RenderIf
                condition={
                  formik.touched.communityEventTypeId &&
                  formik.errors.communityEventTypeId
                }
                renderElse={null}>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.communityEventTypeId}
                  </div>
                </div>
              </RenderIf>
            </div>

            <div className="row mt-3">
              <div className="col-12 col-md-12 col-lg-4">
                <DatePickers
                  selected={date}
                  id="date"
                  name="date"
                  dateFormat="dd-MM-yyyy"
                  onChange={(value) => {
                    setDate(value);
                    formik.setFieldValue("date", formatDate(value));
                  }}
                  placeholderText={t("date")}
                />
                <RenderIf
                  condition={formik.touched.date && formik.errors.date}
                  renderElse={null}>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.date}</div>
                  </div>
                </RenderIf>
              </div>
              <div className="col-12 col-md-12 col-lg-4">
                <DatePicker
                  selected={fromClock}
                  id="fromClock"
                  name="fromClock"
                  dateFormat="h:mm aa"
                  onChange={(value) => {
                    setFromClock(value);
                    formik.setFieldValue("fromClock", value);
                  }}
                  placeholderText={t("startDate")}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={5}
                  timeCaption="Time"
                />
                <RenderIf
                  condition={
                    formik.touched.fromClock && formik.errors.fromClock
                  }
                  renderElse={null}>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      {formik.errors.fromClock}
                    </div>
                  </div>
                </RenderIf>
              </div>
              <div className="col-12 col-md-12 col-lg-4">
                <DatePicker
                  selected={toClock}
                  id="toClock"
                  name="toClock"
                  dateFormat="h:mm aa"
                  onChange={(value) => {
                    setToClock(value);
                    formik.setFieldValue("toClock", value);
                  }}
                  placeholderText={t("endDate")}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={5}
                  timeCaption="Time"
                />
                <RenderIf
                  condition={formik.touched.toClock && formik.errors.toClock}
                  renderElse={null}>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.toClock}</div>
                  </div>
                </RenderIf>
              </div>
            </div>
            <div className="w-100 col-12 col-md-12 col-lg-6 mt-3">
              <div className="d-flex">
                <input placeholder="Photo" disabled />
                <div className="image-upload">
                  <input
                    accept="image/*"
                    id="contained-button-file"
                    type="file"
                    name="file"
                    label={t("Photo")}
                    placeholder={t("Photo")}
                    className="w-100"
                    onChange={imagePreview}
                  />
                  <Download />
                </div>
              </div>
              <RenderIf condition={imageSrc}>
                <div className="image-preview mt-4">
                  <div
                    style={{
                      position: "relative",
                      width: "100px",
                      height: "100px",
                    }}>
                    <img
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "100%",
                      }}
                      src={imageSrc}
                      alt="img"
                    />
                    <button
                      style={{
                        position: "absolute",
                        right: "-30px",
                      }}
                      className="BtnSvg"
                      onClick={onClick}>
                      <Add />
                    </button>
                  </div>
                </div>
              </RenderIf>

              <RenderIf
                condition={formik.touched.file && formik.errors.file}
                renderElse={null}>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.file}</div>
                </div>
              </RenderIf>
            </div>

            <div className="w-100 pt-2 pb-3 mt-3">
              <RichTextEditor
                onChange={(content) => {
                  handleContentChange(content);
                  formik.setFieldValue("description", content);
                }}
                value={content}
              />
            </div>
            <RenderIf
              condition={
                formik.touched.description && formik.errors.description
              }
              renderElse={null}>
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.description}</div>
              </div>
            </RenderIf>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="pt_14_26 font_14 align-items-center d-flex"
            background="#057953"
            type="submit"
            disabled={isLoading}>
            {t("createEvent")}
            <RenderIf condition={isLoading}>
              <div class="spinner-border" role="status"></div>{" "}
            </RenderIf>
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
export default CreateCommunityEvents;
